import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { waterApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { formatNumber } from '../lib/format.js';
import { DropletIcon } from './icons.js';

interface WaterCardProps {
  /** Local date being viewed, YYYY-MM-DD. */
  date: string;
  timeZone: string;
  goalMl: number | null;
  /** Only today can be logged to; past days are shown read-only. */
  editable: boolean;
}

/** The one-tap amounts that cover almost every logging action. */
const QUICK_ADD = [
  { ml: 250, label: 'Glas' },
  { ml: 500, label: 'Flasche' },
];

/**
 * Daily water intake: a running total against the goal, with one-tap glasses
 * and bottles. Editing is limited to today, matching how the counter is used —
 * you log water as you drink it, not retroactively for last Tuesday.
 */
export function WaterCard({ date, timeZone, goalMl, editable }: WaterCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [custom, setCustom] = useState('');

  const dayQuery = useQuery({
    queryKey: ['water', 'day', date],
    queryFn: () => waterApi.day({ date, timezone: timeZone }),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['water', 'day', date] }).then(() => undefined);

  const addWater = useMutation({
    mutationFn: (ml: number) => waterApi.add({ ml }),
    onSuccess: async () => {
      setError(null);
      setCustom('');
      await invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.'),
  });

  const removeWater = useMutation({
    mutationFn: (id: string) => waterApi.remove(id),
    onSuccess: invalidate,
  });

  const day = dayQuery.data;
  const totalMl = day?.totalMl ?? 0;
  const goal = goalMl ?? day?.goalMl ?? null;
  const percent = goal && goal > 0 ? Math.min(100, (totalMl / goal) * 100) : 0;
  const lastEntry = day?.entries.at(-1);

  function addCustom(): void {
    const parsed = Math.round(Number(custom.replace(',', '.')));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte gib eine gültige Menge in ml ein.');
      return;
    }
    addWater.mutate(parsed);
  }

  return (
    <div className="card stack">
      <div className="row-between">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DropletIcon />
          <span>Wasser</span>
        </div>
        <span className="hint">
          {formatNumber(totalMl)} ml{goal ? ` / ${formatNumber(goal)} ml` : ''}
        </span>
      </div>

      {goal != null && goal > 0 && (
        <div className="macro-bar" style={{ height: 8 }}>
          <span style={{ width: `${percent}%`, background: 'var(--water)' }} />
        </div>
      )}

      {editable && (
        <>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {QUICK_ADD.map(({ ml, label }) => (
              <button
                key={ml}
                className="btn btn-ghost grow"
                disabled={addWater.isPending}
                onClick={() => addWater.mutate(ml)}
              >
                +{ml} ml
                <span className="hint" style={{ marginLeft: 6 }}>
                  {label}
                </span>
              </button>
            ))}
          </div>

          <div className="row">
            <input
              className="input grow"
              inputMode="numeric"
              placeholder="ml"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addCustom();
              }}
            />
            <button
              className="btn btn-primary"
              disabled={addWater.isPending || custom.trim().length === 0}
              onClick={addCustom}
            >
              {addWater.isPending ? <span className="spinner" /> : 'Hinzufügen'}
            </button>
          </div>

          {lastEntry && (
            <button
              className="btn btn-ghost btn-sm"
              disabled={removeWater.isPending}
              onClick={() => removeWater.mutate(lastEntry.id)}
            >
              Letzte Eingabe ({formatNumber(lastEntry.ml)} ml) rückgängig
            </button>
          )}
        </>
      )}

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
